<template>
  <div
    v-click-outside="close"
    class="app-bar-search"
  >
    <!-- Collapsed: just the icon -->
    <v-btn
      v-if="!isOpen"
      icon
      class="search-icon-btn app-bar-icon-button"
      :aria-label="$t(&quot;Search Self Biography&quot;)"
      @click="open"
    >
      <v-icon>{{ icons.mdiMagnify }}</v-icon>
    </v-btn>

    <!-- Expanded: search input + results -->
    <div
      v-else
      class="search-container"
    >
      <div class="search-input-wrapper">
        <v-icon
          size="20"
          class="search-input-icon"
          @click="close"
        >
          {{ icons.mdiArrowLeft }}
        </v-icon>
        <input
          ref="searchInput"
          v-model="query"
          type="text"
          :placeholder="$t(&quot;Search SelfBiography&quot;)"
          class="search-input"
          @input="onInput"
          @keydown.esc="close"
          @keydown.down.prevent="highlightNext"
          @keydown.up.prevent="highlightPrev"
          @keydown.enter.prevent="selectHighlighted"
        />
        <v-icon
          v-if="query"
          size="18"
          class="search-clear-icon"
          @click="clearQuery"
        >
          {{ icons.mdiClose }}
        </v-icon>
      </div>

      <!-- Results dropdown -->
      <div
        v-if="showResults"
        class="search-results"
      >
        <!-- Recent searches -->
        <div
          v-if="!query && recentSearches.length"
          class="search-section"
        >
          <div class="search-section-header">
            <span class="font-weight-semibold text-sm">{{ $t("Recent") }}</span>
            <a
              class="text-sm primary--text"
              style="cursor: pointer;"
              @click="clearRecent"
            >{{ $t("Clear all") }}</a>
          </div>
          <div
            v-for="(user, i) in recentSearches"
            :key="'r-' + user.id"
            class="search-result-item"
            :class="{ 'search-result-highlighted': highlightedIndex === i }"
            @click="goToProfile(user)"
            @mouseenter="highlightedIndex = i"
          >
            <v-avatar
              size="36"
              class="me-3"
            >
              <v-img :src="getAvatarUrl(user.avatar)"></v-img>
            </v-avatar>
            <div class="search-result-info">
              <div class="text-sm font-weight-semibold">
                {{ user.name }}
              </div>
              <div class="text-xs text--secondary">
                @{{ user.username }}
              </div>
            </div>
            <v-icon
              size="16"
              class="search-remove-recent"
              @click.stop="removeRecent(user.id)"
            >
              {{ icons.mdiClose }}
            </v-icon>
          </div>
        </div>

        <!-- Loading -->
        <div
          v-if="loading"
          class="search-loading"
        >
          <v-progress-circular
            indeterminate
            size="24"
            color="primary"
          ></v-progress-circular>
        </div>

        <!-- Search results -->
        <div
          v-if="query && !loading && results.length"
          class="search-section"
        >
          <div
            v-for="(user, i) in results"
            :key="user.id"
            class="search-result-item"
            :class="{ 'search-result-highlighted': highlightedIndex === i }"
            @click="goToProfile(user)"
            @mouseenter="highlightedIndex = i"
          >
            <v-avatar
              size="36"
              class="me-3"
            >
              <v-img :src="getAvatarUrl(user.avatar)"></v-img>
            </v-avatar>
            <div class="search-result-info">
              <div
                class="text-sm font-weight-semibold"
                v-html="highlightMatch(user.name)"
              ></div>
              <div
                class="text-xs text--secondary"
                v-html="'@' + highlightMatch(user.username)"
              ></div>
            </div>
          </div>
        </div>

        <!-- No results -->
        <div
          v-if="query && !loading && !results.length && searched"
          class="search-no-results"
        >
          <span class="text-sm text--secondary">{{ $t("No results found for \"") }}{{ query }}"</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mdiMagnify, mdiArrowLeft, mdiClose } from '@mdi/js'
import axios from 'axios'

const RECENT_STORAGE_KEY = 'sb_recent_searches'

export default {
  data: () => ({
    isOpen: false,
    query: '',
    results: [],
    loading: false,
    searched: false,
    highlightedIndex: -1,
    debounceTimer: null,
    recentSearches: [],
    showResults: false,
    endpoint: process.env.VUE_APP_ROOT_API,
    defaultAvatar: require('@/assets/images/avatars/1.png'),
    icons: {
      mdiMagnify,
      mdiArrowLeft,
      mdiClose,
    },
  }),
  mounted() {
    this.loadRecent()
  },
  methods: {
    getAuthHeaders() {
      const token = this.$store.state.jwtToken

      return token ? { Authorization: token } : {}
    },

    open() {
      this.isOpen = true
      this.showResults = true
      this.$nextTick(() => {
        this.$refs.searchInput && this.$refs.searchInput.focus()
      })
    },

    close() {
      this.isOpen = false
      this.query = ''
      this.results = []
      this.searched = false
      this.highlightedIndex = -1
      this.showResults = false
    },

    clearQuery() {
      this.query = ''
      this.results = []
      this.searched = false
      this.highlightedIndex = -1
      this.$refs.searchInput && this.$refs.searchInput.focus()
    },

    onInput() {
      this.highlightedIndex = -1
      clearTimeout(this.debounceTimer)
      if (!this.query || this.query.trim().length < 2) {
        this.results = []
        this.searched = false

        return
      }
      this.debounceTimer = setTimeout(() => {
        this.search()
      }, 300)
    },

    async search() {
      const q = this.query.trim()
      if (q.length < 2) return
      this.loading = true
      this.searched = false
      try {
        const res = await axios.get(
          `${this.endpoint}/searchUsers?q=${encodeURIComponent(q)}&limit=8`,
          { headers: this.getAuthHeaders() },
        )
        this.results = res.data.users || []
      } catch {
        this.results = []
      }
      this.loading = false
      this.searched = true
    },

    goToProfile(user) {
      this.saveRecent(user)
      this.close()
      this.$router.push({ name: 'public_user', params: { username: user.username } }).catch(() => {})
    },

    highlightNext() {
      const list = this.query ? this.results : this.recentSearches
      if (list.length === 0) return
      this.highlightedIndex = (this.highlightedIndex + 1) % list.length
    },

    highlightPrev() {
      const list = this.query ? this.results : this.recentSearches
      if (list.length === 0) return
      this.highlightedIndex = this.highlightedIndex <= 0 ? list.length - 1 : this.highlightedIndex - 1
    },

    selectHighlighted() {
      const list = this.query ? this.results : this.recentSearches
      if (this.highlightedIndex >= 0 && this.highlightedIndex < list.length) {
        this.goToProfile(list[this.highlightedIndex])
      }
    },

    getAvatarUrl(avatar) {
      if (avatar) {
        return `https://self-biography-system.s3.us-east-2.amazonaws.com/${avatar}`
      }

      return this.defaultAvatar
    },

    highlightMatch(text) {
      if (!text || !this.query) return text
      const escaped = this.query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const regex = new RegExp(`(${escaped})`, 'gi')

      return text.replace(regex, '<strong>$1</strong>')
    },

    // Recent searches persistence
    loadRecent() {
      try {
        const stored = localStorage.getItem(RECENT_STORAGE_KEY)
        this.recentSearches = stored ? JSON.parse(stored) : []
      } catch {
        this.recentSearches = []
      }
    },

    saveRecent(user) {
      const entry = {
        id: user.id, name: user.name, username: user.username, avatar: user.avatar,
      }
      this.recentSearches = [entry, ...this.recentSearches.filter(u => u.id !== user.id)].slice(0, 5)
      localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(this.recentSearches))
    },

    removeRecent(userId) {
      this.recentSearches = this.recentSearches.filter(u => u.id !== userId)
      localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(this.recentSearches))
    },

    clearRecent() {
      this.recentSearches = []
      localStorage.removeItem(RECENT_STORAGE_KEY)
    },
  },
}
</script>

<style lang="scss" scoped>
.app-bar-search {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon-btn {
  cursor: pointer;
}

.search-container {
  position: relative;
}

.search-input-wrapper {
  display: flex;
  align-items: center;
  background: #f0f2f5;
  border-radius: 50px;
  padding: 4px 12px;
  width: 280px;
  transition: width 0.2s ease;

  .search-input-icon {
    cursor: pointer;
    margin-right: 8px;
    flex-shrink: 0;
  }

  .search-input {
    border: none;
    outline: none;
    background: transparent;
    font-size: 14px;
    flex: 1;
    min-width: 0;
    color: #1c1e21;

    &::placeholder {
      color: #8a8d91;
    }
  }

  .search-clear-icon {
    cursor: pointer;
    flex-shrink: 0;
    margin-left: 4px;
    opacity: 0.5;

    &:hover {
      opacity: 1;
    }
  }
}

.search-results {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  max-height: 400px;
  overflow-y: auto;
  z-index: 10;
  min-width: 280px;
}

.search-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px 4px;
}

.search-result-item {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  cursor: pointer;
  transition: background-color 0.1s;
  position: relative;

  &:hover,
  &.search-result-highlighted {
    background-color: #f2f2f2;
  }

  .search-result-info {
    flex: 1;
    min-width: 0;
    overflow: hidden;

    div {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .search-remove-recent {
    opacity: 0;
    flex-shrink: 0;
    margin-left: 8px;
  }

  &:hover .search-remove-recent {
    opacity: 0.5;

    &:hover {
      opacity: 1;
    }
  }
}

.search-loading {
  display: flex;
  justify-content: center;
  padding: 20px;
}

.search-no-results {
  padding: 20px 16px;
  text-align: center;
}
</style>

<style lang="scss">
// Dark mode overrides
.theme--dark {
  .search-input-wrapper {
    background: #3a3b3c;

    .search-input {
      color: #e4e6eb;

      &::placeholder {
        color: #b0b3b8;
      }
    }
  }

  .search-results {
    background: #242526;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.3);
  }

  .search-result-item {
    &:hover,
    &.search-result-highlighted {
      background-color: #3a3b3c;
    }
  }
}
</style>
